import { type FC } from "react";
import { Box, Divider, List, ListItem, Stack, type Theme, Typography, useTheme } from "@mui/material";
import HorizontalSplitOutlinedIcon from "@mui/icons-material/HorizontalSplitOutlined";
import { FormattedMessage } from "react-intl";
import type { Machine } from "@packsize/schemas/machines";

interface MachineTracksProps {
	readonly machine: Machine;
}

// Type for tracks from machine-v2 schema
type MachineTrack = {
	number: number;
	isEmpty: boolean;
	assignedMaterial: {
		id: string;
		alias: string;
		width: number;
		thickness: number;
		quality: number;
	};
	corrugateTolerance?: number;
	width?: number;
	trackOffset?: number;
};

const getTrackStatus = (track: MachineTrack, theme: Theme): { label: string; color: string } => {
	if (track.isEmpty === true) {
		return { label: "empty", color: theme.palette.error.main };
	}
	if (track.assignedMaterial) {
		return { label: "loaded", color: theme.palette.success.main };
	}
	return { label: "unknown", color: theme.palette.info.main };
};

export const MachineTracks: FC<MachineTracksProps> = ({ machine }) => {
	const theme = useTheme();
	const tracks = "tracks" in machine ? machine.tracks : undefined;

	if (!tracks || tracks.length === 0) {
		return (
			<Box sx={{ padding: 2 }}>
				<Typography variant="body2" color="text.secondary">
					<FormattedMessage id="No tracks available" defaultMessage="No tracks available" />
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{ padding: 1, height: "fit-content", overflow: "auto", border: 1, borderRadius: 1, borderColor: "divider" }}
		>
			<Typography variant="h5" sx={{ marginBottom: 1 }}>
				<FormattedMessage id="Tracks" defaultMessage="Tracks" />
			</Typography>
			<Divider />
			<List dense disablePadding>
				{tracks.map((track, index) => {
					const trackNumber = track.number;
					const trackNumberStr = String(trackNumber);
					const assignedMaterial = track.assignedMaterial;
					const materialAlias = assignedMaterial?.alias || assignedMaterial?.id || "No material";
					const status = getTrackStatus(track, theme);

					return (
						<ListItem
							key={`track-${trackNumberStr}`}
							sx={{
								borderBottom: index < tracks.length - 1 ? 1 : 0,
								borderColor: "divider",
							}}
						>
							<Stack direction="row" spacing={1} alignItems="center" width="100%">
								<Typography variant="body2">{trackNumberStr}</Typography>

								<Typography variant="caption" sx={{ flex: 1 }}>
									{materialAlias}
								</Typography>

								<HorizontalSplitOutlinedIcon sx={{ fontSize: 30, color: status.color }} />
							</Stack>
						</ListItem>
					);
				})}
			</List>
		</Box>
	);
};
